import { ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';

// 基本コンテンツカード
interface ContentCardProps {
  children: ReactNode;
  title?: string;
  description?: string;
  className?: string;
  variant?: 'default' | 'outlined' | 'filled' | 'elevated';
  padding?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
}

export const ContentCard: React.FC<ContentCardProps> = ({
  children,
  title,
  description,
  className,
  variant = 'default',
  padding = 'md',
  onClick
}) => {
  const variantClasses = {
    default: 'border bg-card text-card-foreground',
    outlined: 'border-2 bg-transparent',
    filled: 'bg-gray-50 dark:bg-gray-800 border-0',
    elevated: 'border bg-card text-card-foreground shadow-lg'
  };

  const paddingClasses = {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  };

  return (
    <Card
      className={cn(
        variantClasses[variant],
        paddingClasses[padding],
        onClick && 'cursor-pointer hover:shadow-md transition-shadow',
        className
      )}
      onClick={onClick}
    >
      {(title || description) && (
        <CardHeader className="pb-4">
          {title && <CardTitle className="text-lg font-semibold">{title}</CardTitle>}
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
      )}
      <CardContent className={cn(title || description ? 'pt-0' : '')}>
        {children}
      </CardContent>
    </Card>
  );
};

// 情報表示カード
interface InfoCardProps extends Omit<ContentCardProps, 'variant'> {
  icon?: ReactNode;
  variant?: 'info' | 'success' | 'warning' | 'error';
}

export const InfoCard: React.FC<InfoCardProps> = ({
  variant = 'info',
  icon,
  className,
  ...props
}) => {
  const variantStyles = {
    info: {
      card: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950',
      icon: 'text-blue-600 dark:text-blue-400',
      defaultIcon: Info
    },
    success: {
      card: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950',
      icon: 'text-green-600 dark:text-green-400',
      defaultIcon: CheckCircle
    },
    warning: {
      card: 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950',
      icon: 'text-yellow-600 dark:text-yellow-400',
      defaultIcon: AlertTriangle
    },
    error: {
      card: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950',
      icon: 'text-red-600 dark:text-red-400',
      defaultIcon: AlertCircle
    }
  };

  const styles = variantStyles[variant];
  const IconComponent = styles.defaultIcon;

  return (
    <ContentCard
      className={cn(styles.card, className)}
      {...props}
    >
      <div className="flex items-start space-x-3">
        <div className={cn('flex-shrink-0 mt-0.5', styles.icon)}>
          {icon || <IconComponent className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          {props.children}
        </div>
      </div>
    </ContentCard>
  );
};

// インタラクティブカード
interface InteractiveCardProps extends ContentCardProps {
  isHoverable?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
}

export const InteractiveCard: React.FC<InteractiveCardProps> = ({
  isHoverable = true,
  isSelected = false,
  onSelect,
  className,
  ...props
}) => {
  return (
    <ContentCard
      className={cn(
        isHoverable && 'hover:shadow-md hover:scale-[1.02] transition-all duration-200',
        isSelected && 'ring-2 ring-blue-500 border-blue-500',
        'cursor-pointer',
        className
      )}
      onClick={onSelect || props.onClick}
      {...props}
    />
  );
};

// 専用カードバリアント
export const WarningCard: React.FC<Omit<InfoCardProps, 'variant'>> = (props) => (
  <InfoCard variant="warning" {...props} />
);

export const ErrorCard: React.FC<Omit<InfoCardProps, 'variant'>> = (props) => (
  <InfoCard variant="error" {...props} />
);

export const SuccessCard: React.FC<Omit<InfoCardProps, 'variant'>> = (props) => (
  <InfoCard variant="success" {...props} />
);